import { Alert, Link, Text } from "@merge-api/merge-javascript-shared";
import { MoreHorizontal } from "lucide-react";
import * as React from "react";
import {
  Connector
} from "../../../config/types";
import AuthenticationInstructions from "../../shared/AuthenticationInstructions";
import { logLinkActivity } from "../../../utils/fetchUtils";
import useAgentHandlerLinkModalContext from "../../context/useAgentHandlerLinkModalContext";

type Props = {
  connector: Connector;
  header?: React.ReactNode;
  subHeader?: string | React.ReactNode | null;
  description?: string;
  stepHelpGuideURL?: string;
  scrollHeight?: number;
  shouldShowLinkGuideInHeader?: boolean;
  shouldShowHelpCenterButton?: boolean;
};

const ConnectorSetupStepFormHeader = ({
  connector,
  header,
  subHeader,
  description,
  stepHelpGuideURL,
  scrollHeight,
  shouldShowLinkGuideInHeader = false,
  shouldShowHelpCenterButton = false,
}: Props) => {
  const { translate, coBrandingLogo, linkToken, apiBaseURL } =
    useAgentHandlerLinkModalContext();

  const hasDescription = description && !["", null].includes(description);

  const credentialsRegenerated = false;

  return (
    <>
      {coBrandingLogo ? (
        <div className="w-full flex justify-center my-7">
          <div className="flex items-center justify-center space-x-2">
            <img
              className="w-12 h-12 object-cover rounded-[12px] border-[0.5px] border-black border-opacity-[.08] shadow-[0px_5px_8px_-3px_#0000000D,0px_0.5px_1px_0px_#00000017]"
              src={coBrandingLogo}
              alt=""
            />
            <MoreHorizontal className="text-gray-20" size={36} />
            <img
              className="w-12 h-12 object-cover rounded-[12px] border-[0.5px] border-black border-opacity-[.08] shadow-[0px_5px_8px_-3px_#0000000D,0px_0.5px_1px_0px_#00000017]"
              src={connector.logo_url}
              alt=""
            />
          </div>
        </div>
      ) : (
        <div className="px-5 flex flex-shrink-0 items-center justify-center my-9">
          <img className="max-w-[200px] max-h-8" src={connector.logo_url} alt="" />
        </div>
      )}
      {header && (
        <div className="px-5 mb-3 w-full text-center">
          <Text variant="h4">{header}</Text>
          {credentialsRegenerated && (
            <Alert showWarningIcon color="red" className="mt-3">
              <div className="font-normal text-sm">
                {translate(
                  "updated-service-accout-credentials-alert-1",
                  "Use the updated credentials below on a new service account,"
                )}{" "}
                <span className="font-semibold">
                  {" "}
                  {translate(
                    "updated-service-accout-credentials-alert-2",
                    "not an existing account"
                  )}
                </span>
              </div>
            </Alert>
          )}
          {subHeader && (
            <Text variant="md" as="p" className="mt-2 text-gray-40 text-center">
              {subHeader}
            </Text>
          )}
        </div>
      )}
      {hasDescription && (
        <AuthenticationInstructions
          description={description}
          scrollHeight={scrollHeight}
        />
      )}

      {shouldShowLinkGuideInHeader && !shouldShowHelpCenterButton && (
        <div className="px-5 text-center mb-4">
          <Link
            variant="sm"
            className="text-blue-40"
            href={stepHelpGuideURL}
            onClick={() => {
              try {
                const eventName = stepHelpGuideURL
                  ? stepHelpGuideURL.split("/").pop() ?? connector?.name ?? "integration"
                  : (connector?.name ?? "integration") + "_fallback";
                apiBaseURL &&
                  linkToken &&
                  logLinkActivity({
                    apiBaseURL,
                    linkToken,
                    eventName,
                  });
              } catch (error) {}
            }}
          >
            {translate(
              "stuck-link-integ-setup",
              "Stuck? See detailed instructions with screenshots"
            )}
          </Link>
        </div>
      )}
    </>
  );
};

export default ConnectorSetupStepFormHeader;
